import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import type { ProductLine } from '@popcart/core/v1';
import { EmptyState, ErrorState, Loading, useTitle } from '../../ui/bits';
import { STATE_ART } from '../../lib/voice.js';
import { ProductEditor } from '../../catalog/ProductEditor';
import { useOpsShops, type OpsShop } from '../queries';
import { Select } from '../../ui/controls';

/** /ops/products/new?shop= and /ops/products/:shopRef/:productId (the shop's short name; an id link is
 *  replaced with it) — the shared editor for that shop. */
export function OpsProductEditPage() {
  const { shopRef: routeShop, productId } = useParams();
  const [params] = useSearchParams();
  const shops = useOpsShops();
  const shopId = routeShop || params.get('shop') || '';

  if (shops.isError)
    return (
      <section className="card">
        <ErrorState error={shops.error} onRetry={() => void shops.refetch()} />
      </section>
    );
  if (!shops.data) return <Loading label="Loading" rows={3} />;
  const shop = shops.data.find((s) => s.id === shopId || (!!s.slug && s.slug === shopId));
  const pathFor = (s: OpsShop, id: string) => `/ops/products/${encodeURIComponent(s.slug || s.id)}/${encodeURIComponent(id)}`;
  if (shop && productId && routeShop && routeShop !== (shop.slug || shop.id)) return <Navigate replace to={pathFor(shop, productId)} />;
  if (!productId && !shop) return <PickShop shops={shops.data} />;
  if (!shop)
    return (
      <section className="card">
        <EmptyState art={STATE_ART.notFound} title="That shop isn’t in PopCart" action={<Link className="btn" to="/ops/products">Back to products</Link>} />
      </section>
    );
  return (
    <ProductEditor
      key={productId ?? 'new'}
      shopId={shop.id}
      productId={productId ?? null}
      lines={shop.lines}
      shopName={shop.name.toUpperCase()}
      backTo={`/ops/products?shop=${encodeURIComponent(shop.id)}`}
      editPathFor={(id) => pathFor(shop, id)}
      initialLine={params.get('line') as ProductLine | null}
    />
  );
}

/** Ops adding a product says which shop sells it first. */
function PickShop({ shops }: { shops: OpsShop[] }) {
  useTitle('New product');
  const navigate = useNavigate();
  const [id, setId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const sorted = shops.slice().sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
      <div className="top">
        <div className="grow stack s4">
          <h1 className="t-title">New product</h1>
          <span className="t-monocap muted">STEP 1 · WHICH SHOP SELLS IT?</span>
        </div>
      </div>
      <section className="card panel">
        <form
          className="row wrap"
          onSubmit={(e) => {
            e.preventDefault();
            if (!id) {
              setErr('Pick a shop.');
              return;
            }
            navigate(`/ops/products/new?shop=${encodeURIComponent(id)}`, { replace: true });
          }}
        >
          <div className="field grow">
            <label htmlFor="pick-shop">Shop</label>
            <Select
              id="pick-shop"
              className="input"
              value={id}
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? 'pick-shop-err' : undefined}
              onChange={(e) => {
                setId(e.target.value);
                setErr(null);
              }}
            >
              <option value="">Choose a shop</option>
              {sorted.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.status !== 'live' ? ` (${s.status})` : ''}
                </option>
              ))}
            </Select>
            {err ? (
              <span id="pick-shop-err" className="pill st-error err-pill">
                {err}
              </span>
            ) : null}
          </div>
          <button type="submit" className="btn btn-primary">
            Continue
          </button>
        </form>
      </section>
    </>
  );
}
